import React, { useCallback } from 'react';
import Grid from '@/components/Grid';
import SeqConsultOptionButton from './SeqConsultOptionButton';
import useSeqConsultQuestion from './hooks/useSeqConsultQuestion';
import { useSeqConsultContext } from '@/providers/SeqConsultProvider';
import {
  Box,
  Text,
  Input,
  CheckBox,
  DotBadge,
  HelperText,
  Textarea,
  HStack,
  NewContentButton,
  VStack,
  NewBasicButton,
  Icongraphy,
  NewTextButton,
  LinePopup,
} from '@design-system/pc';
import { getFieldErrorMessage, hasFieldError } from '@/utils';
import SeqSearchAddressForm from './SeqSearchAddressForm';
import {
  CERTIFY_ERROR_MESSAGE,
  CONSULT_NAME_PATTERN,
} from '@/constants/seq-consult';
import { CertificationStatus } from '@/hooks/useTelCertification';
import useAuthenticated from '@/hooks/useAuthenticated';
import styled from 'styled-components';
import { HEADER_Z_INDEX } from '@/styles';

interface SeqConsultQuestionProps {}
const SeqConsultQuestion = (props: SeqConsultQuestionProps) => {
  const {
    workAvailableAreas,
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
  } = useSeqConsultContext();
  const {
    step,
    watch,
    errors,
    register,
    handleClick,
    isShowEtcInput,
    // allFlagChecked,
    // handleChangeAllScopes,
    getActive,
    handleAllAgreement,
    allAgreementChecked,
    handleAgreementChecked,
    handleAgreementPopup,
    handleChangeTel,
    user,
    handleChangeEtc,
    handleChangeName,
    certificationNumber,
    handleChangeCertificationNumber,
    handleSendTel,
    count,
    status,
    handleSendCertificationNumber,
    isTelDisabled,
    handleEnableTelInput,
    currentStepData,
  } = useSeqConsultQuestion();
  const { isAuthenticated } = useAuthenticated();

  const renderRightPart = useCallback(
    (status: CertificationStatus) => {
      if (isAuthenticated) {
        return <></>;
      }

      if (status === 'CERTIFIED') {
        return (
          <NewBasicButton
            type="button"
            emphasis="low"
            size="lg"
            width="119px"
            onClick={handleEnableTelInput}
          >
            <Text variant="label_lg_r">변경</Text>
          </NewBasicButton>
        );
      }

      return (
        <NewBasicButton
          type="button"
          emphasis="low"
          size="lg"
          onClick={handleSendTel}
          minWidth="119px"
        >
          <Text variant="label_lg_r">
            {status === 'BEFORE_SEND' ? '인증번호 전송' : '재전송'}
          </Text>
        </NewBasicButton>
      );
    },
    [handleEnableTelInput, handleSendTel, isAuthenticated],
  );

  if (!currentStepData && step !== 'personal') {
    return null;
  }

  type StepItem = {
    name: string;
    value: string | number;
  };

  const renderAnswerBasicGrid = (data: StepItem[], colSize: number = 2) => (
    <Grid colSize={colSize} colGap={20} rowGap={20}>
      {data.map((item) => (
        <SeqConsultOptionButton
          isActive={getActive(item.value)}
          key={`seq-question-button-${item.value}`}
          onClick={() => handleClick(item.value)}
        >
          {item.name}
        </SeqConsultOptionButton>
      ))}
    </Grid>
  );

  if (
    step === 'baseConstructionPlan.age' ||
    step === 'site.buildingType' ||
    step === 'site.houseNMPG' ||
    step === 'baseConstructionPlan.spaceStatus' ||
    step === 'baseConstructionPlan.consultPurpose' ||
    step === 'baseConstructionPlan.consultExperience' ||
    step === 'baseConstructionPlan.opinionReflect'
  ) {
    return renderAnswerBasicGrid(currentStepData?.data || []);
  }

  if (
    step === 'baseConstructionPlan.communicationStyle' ||
    step === 'baseConstructionPlan.budgetOption'
  ) {
    return renderAnswerBasicGrid(currentStepData?.data || [], 1);
  }

  if (step === 'baseConstructionPlan.scopes') {
    return (
      <Box>
        {/* <Box mb="20px">
          <CheckBox
            checked={allFlagChecked}
            labelOptions={{
              text: '전체 공간',
              textVariant: 'body1',
            }}
            onChange={handleChangeAllScopes}
          />
        </Box> */}
        <Grid colSize={3} colGap={20} rowGap={24}>
          {currentStepData?.data?.map((item) => (
            <SeqConsultOptionButton
              variant="photo"
              isActive={getActive(item.value)}
              key={`seq-question-button-${item.value}`}
              onClick={() => handleClick(item.value)}
              imgSrc={`/assets/images/seq-consult/scope/${String(
                item.value,
              ).toLowerCase()}.jpg`}
            >
              {item.name}
            </SeqConsultOptionButton>
          ))}
        </Grid>

        {isShowEtcInput && (
          <Box mt="20px">
            <DotBadge
              offset={{
                right: -4,
                top: 0,
              }}
              size="sm"
              zIndex={50}
            >
              <Text variant="subTitle6" mb="12px">
                기타 내용을 입력해주세요 (20자 이내)
              </Text>
            </DotBadge>

            <Input
              size="lg"
              {...register('baseConstructionPlan.etcComment', {
                required: isShowEtcInput,
                pattern: {
                  value: /^[^ㄱ-ㅎㅏ-ㅣ]{1,20}$/g,
                  message: '내용을 정확히 입력해주세요.',
                },
                onChange: (e) => handleChangeEtc(e),
              })}
              placeholder="ex) 주방수전교체, 시스템에어컨"
              maxLength={20}
              isError={hasFieldError(errors, 'baseConstructionPlan.etcComment')}
              errorMessage={getFieldErrorMessage(
                errors,
                'baseConstructionPlan.etcComment',
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  if (step === 'baseConstructionPlan.budget') {
    return (
      <Grid colSize={2} colGap={20} rowGap={20}>
        {currentStepData?.data?.map((item) => (
          <SeqConsultOptionButton
            isActive={getActive(item.value)}
            key={`seq-question-button-${item.value}`}
            onClick={() => handleClick(item.value)}
          >
            {item.name}
          </SeqConsultOptionButton>
        ))}
      </Grid>
    );
  }

  if (step === 'baseConstructionPlan.consultSchedule') {
    return (
      <Grid colSize={2} colGap={20} rowGap={20}>
        {currentStepData?.data?.map((item) => (
          <SeqConsultOptionButton
            isActive={getActive(item.value)}
            key={`seq-question-button-${item.value}`}
            onClick={() => handleClick(item.value)}
          >
            {item.name}
          </SeqConsultOptionButton>
        ))}
      </Grid>
    );
  }

  if (step === 'baseConstructionPlan.desiredDay') {
    return (
      <Box>
        <Grid colSize={2} colGap={20} rowGap={20}>
          {currentStepData?.data?.map((item) => (
            <SeqConsultOptionButton
              isActive={getActive(item.value)}
              key={`seq-question-button-${item.value}`}
              onClick={() => handleClick(item.value)}
            >
              {item.name}
            </SeqConsultOptionButton>
          ))}
        </Grid>
        <PeriodStepNotice
          mt="48px"
          pt="16px"
          pb="16px"
          borderTop="1px solid"
          borderColor="color_chip.gray.200"
        >
          <Box as="ul">
            <Box as="li">
              <Text variant="body2" color="color_chip.gray.800">
                주문량이 몰리는 경우 시공일이 다소 지연될 수 있습니다.
              </Text>
            </Box>
            <Box as="li">
              <Text variant="body2" color="color_chip.gray.800">
                담당 디자이너를 통해 최선의 일정으로 진행될 수 있도록
                하겠습니다.
              </Text>
            </Box>
          </Box>
        </PeriodStepNotice>
      </Box>
    );
  }

  // if (step === 'site.address') {
  //   return <SeqSearchAddressForm />;
  // }

  if (step === 'personal') {
    return (
      <Box>
        <Box mb="12px">
          <DotBadge
            offset={{
              right: -4,
              top: 0,
            }}
            size="sm"
            zIndex={50}
          >
            <Text variant="subTitle5">이름</Text>
          </DotBadge>
        </Box>
        <Box mb="52px">
          <Input
            type="text"
            size="lg"
            placeholder="이름 입력"
            disabled={!!user.name}
            value={user.name || watch('name')}
            isError={hasFieldError(errors, 'name')}
            errorMessage={getFieldErrorMessage(errors, 'name')}
            maxLength={20}
            {...register('name', {
              required: {
                value: true,
                message: '이름을 정확히 입력해 주세요.',
              },
              pattern: {
                value: new RegExp(CONSULT_NAME_PATTERN),
                message: '이름을 정확히 입력해 주세요.',
              },
              onChange: handleChangeName,
            })}
          />
        </Box>

        <Box mb="12px">
          <DotBadge
            offset={{
              right: -4,
              top: 0,
            }}
            size="sm"
            zIndex={50}
          >
            <Text variant="subTitle5">휴대폰번호</Text>
          </DotBadge>
        </Box>

        <Box mb="52px">
          <HStack space={8} mb="8px" style={{ alignItems: 'flex-start' }}>
            <Input
              flex="1"
              type="text"
              size="lg"
              disabled={!!user.tel || isTelDisabled}
              value={user.tel || watch('tel')}
              placeholder="숫자만 입력"
              maxLength={13}
              {...register('tel', {
                required: {
                  value: true,
                  message: '휴대폰번호를 정확히 입력해 주세요.',
                },
                pattern: {
                  value: /^\d{3}-\d{3,4}-\d{4}$/,
                  message: '휴대폰번호를 정확히 입력해 주세요.',
                },
                onChange: handleChangeTel,
              })}
              isError={
                status === 'EXCEEDED' ||
                status === 'TIME_EXPIRED' ||
                hasFieldError(errors, 'tel')
              }
              errorMessage={
                CERTIFY_ERROR_MESSAGE[status as 'EXCEEDED' | 'TIME_EXPIRED'] ||
                getFieldErrorMessage(errors, 'tel')
              }
            />

            {renderRightPart(status)}
          </HStack>

          {(status === 'CERTIFYING' || status === 'INACCURATE') && (
            <HStack space={8} style={{ alignItems: 'flex-start' }}>
              <Input
                autoFocus
                type="number"
                size="lg"
                flex={1}
                maxLength={6}
                placeholder="인증번호 입력"
                value={certificationNumber}
                onChange={handleChangeCertificationNumber}
                isError={status === 'INACCURATE'}
                errorMessage={CERTIFY_ERROR_MESSAGE.INACCURATE}
                rightPart={
                  <HStack space={4} width="115px">
                    <Icongraphy
                      color="color_chip.primary"
                      resize
                      size={16}
                      useFillColor
                      variant="ic_time"
                    />
                    <Text color="color_chip.primary" variant="body2">
                      남은 시간 {count.text}
                    </Text>
                  </HStack>
                }
              />
              <NewBasicButton
                type="button"
                emphasis="low"
                size="lg"
                width="119px"
                onClick={handleSendCertificationNumber}
              >
                <Text variant="label_lg_r">확인</Text>
              </NewBasicButton>
            </HStack>
          )}
        </Box>

        <Box mb="52px">
          <Box>
            <DotBadge
              offset={{
                right: -4,
                top: 0,
              }}
              size="sm"
              zIndex={50}
            >
              <Text variant="subTitle5">시공 주소</Text>
            </DotBadge>
            {workAvailableAreas && (
              <HStack space={4} mt="4px" mb="12px" alignment="start">
                <NewTextButton
                  icon={{
                    align: 'right',
                    variant: 'ic_alert',
                  }}
                  color="color_chip.blue.500"
                  size="md"
                  onClick={handleOpenBottomSheet}
                  type="button"
                  id="workableAreaButton"
                  position="relative"
                >
                  상담 가능 지역
                </NewTextButton>
                <Text variant="body2" color="color_chip.gray.800">
                  외 신청시 담당자가 변경될 수 있습니다
                </Text>
              </HStack>
            )}
            {!workAvailableAreas && (
              <HelperText variant="caption1" isShow={true} mt="4px" mb="12px">
                인테리어가 필요한 지역의 전문가를 연결해 드릴 예정입니다.
              </HelperText>
            )}
          </Box>

          <SeqSearchAddressForm />
        </Box>

        <Box mb="12px">
          <Text variant="subTitle5">요청사항 (선택)</Text>
        </Box>
        <Box mb="62px">
          <Textarea
            fixedHeight="84px"
            maxLength={300}
            placeholder="ex) 5인가족이라 짐이 많아요. 수납공간을 넉넉하게 배치하고 싶어요."
            {...register('comment')}
          />
        </Box>

        <Box>
          <HStack
            mb="12px"
            height="48px"
            borderBottom="1px solid"
            borderColor="color_chip.primary"
          >
            <CheckBox
              data-cypress-element="all-checkbox"
              id="seq-all-checkbox"
              checked={allAgreementChecked}
              onChange={handleAllAgreement}
            />
            <label
              htmlFor="seq-all-checkbox"
              style={{ marginLeft: '6px', cursor: 'pointer' }}
            >
              <Text as="span" variant="headline6">
                개인정보 수집 동의
              </Text>
            </label>
          </HStack>

          <VStack>
            <HStack space="space-between" width="100%" height="48px">
              <HStack>
                <CheckBox
                  labelOptions={{
                    text: '개인정보 수집 및 이용동의',
                    textVariant: 'body2',
                  }}
                  {...register('agreement.privacy')}
                  checked={watch('agreement.privacy')}
                  onChange={handleAgreementChecked}
                />
                <Text variant="body2" color="color_chip.red.400" ml="4px">
                  (필수)
                </Text>
              </HStack>

              <NewContentButton
                type="button"
                name="privacy"
                emphasis="low"
                size="sm"
                width="72px"
                style={{ borderRadius: '2px' }}
                onClick={handleAgreementPopup}
              >
                보기
              </NewContentButton>
            </HStack>
            <HStack space="space-between" width="100%" height="48px">
              <HStack>
                <CheckBox
                  labelOptions={{
                    text: '개인정보 제3자 제공 동의',
                    textVariant: 'body2',
                  }}
                  {...register('agreement.provide')}
                  checked={watch('agreement.provide')}
                  onChange={handleAgreementChecked}
                />
                <Text variant="body2" color="color_chip.red.400" ml="4px">
                  (필수)
                </Text>
              </HStack>

              <NewContentButton
                type="button"
                name="provide"
                emphasis="low"
                size="sm"
                width="72px"
                style={{ borderRadius: '2px' }}
                onClick={handleAgreementPopup}
              >
                보기
              </NewContentButton>
            </HStack>
          </VStack>
        </Box>

        <StyledLinePopup
          isOpen={isOpenWorkableBottomSheet}
          portalId="workableAreaButton"
          position="absolute"
          top="28px"
          left="0"
          margin="0"
          isControlBGScroll
          isControlBGPointerEvent
          handleClose={handleCloseBottomSheet}
          zIndex={HEADER_Z_INDEX - 1}
        >
          <Box overflow="hidden" py={12} width={362}>
            <BottomSheetHStack>
              {workAvailableAreas?.slice(0, 20).map((item) => {
                return (
                  <Box position="relative" key={`work-available-areas-${item}`}>
                    <Box
                      width="1px"
                      height={12}
                      backgroundColor="color_chip.gray.200"
                      position="absolute"
                      top="2px"
                      left="-13px"
                    ></Box>
                    <Text variant="label_lg_r" color="color_chip.primary">
                      {item}
                    </Text>
                  </Box>
                );
              })}
            </BottomSheetHStack>
          </Box>
        </StyledLinePopup>
      </Box>
    );
  }

  return null;
};

export default SeqConsultQuestion;

const PeriodStepNotice = styled(Box)`
  ${({ theme }) => `
    li {
      position: relative;
      margin-bottom: 4px;
      padding-left: 12px;

      &::after {
        content: '';
        display: block;
        position: absolute;
        width: 4px;
        height: 4px;
        top: 7px;
        left: 0;
        background-color: ${theme.colors.color_chip.gray['500']};
        border-radius: 50%;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  `}
`;

const StyledLinePopup = styled(LinePopup)`
  width: 420px;
  height: auto;
`;

const BottomSheetHStack = styled(HStack)`
  flex-wrap: wrap;
  gap: 12px 25px;
`;
